import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { create, isPlayerSupported } from 'amazon-ivs-player';

interface IVSPlayerProps {
  streamUrl: string | null;
  isLive?: boolean;
  onPlayerLoad?: () => void;
  onPlayerError?: (error: string) => void;
  onPlayerStateChange?: (state: string) => void;
  style?: React.CSSProperties;
  controls?: boolean;
  autoplay?: boolean;
}

export const IVSPlayer: React.FC<IVSPlayerProps> = ({
  streamUrl,
  isLive = true,
  onPlayerLoad,
  onPlayerError,
  onPlayerStateChange,
  style = {},
  controls = false,
  autoplay = true
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [dots, setDots] = useState('');

  // Animation des points de chargement
  useEffect(() => {
    if (isLoading && !isConnected) {
      const interval = setInterval(() => {
        setDots(prev => (prev === '...' ? '' : prev + '.'));
      }, 500);
      return () => clearInterval(interval);
    }
  }, [isLoading, isConnected]);

  // Callbacks pour les événements IVS (définis avant useEffect pour éviter les warnings)
  const handleReady = useCallback(() => {
    setIsLoading(false);
    setError(null);
    onPlayerLoad?.();
  }, [onPlayerLoad]);

  const handleStateChange = useCallback((event: any) => {
    const state = event.state;
    onPlayerStateChange?.(state);

    switch (state) {
      case 'Playing':
        setIsConnected(true);
        setIsLoading(false);
        setError(null);
        break;
      case 'Buffering':
        setIsLoading(true);
        break;
      case 'Idle':
        setIsConnected(false);
        break;
      case 'Ended':
        setIsConnected(false);
        setIsLoading(false);
        break;
    }
  }, [onPlayerStateChange]);

  const handleError = useCallback((event: any) => {
    const errorMsg = `Erreur de lecture: ${event.type}`;
    setError(errorMsg);
    setIsLoading(false);
    setIsConnected(false);
    onPlayerError?.(errorMsg);
  }, [onPlayerError]);

  // Initialisation du player
  useEffect(() => {
    if (!videoRef.current) {
      return;
    }

    if (!isPlayerSupported) {
      const errorMsg = 'IVS Player non supporté dans ce navigateur';
      setError(errorMsg);
      onPlayerError?.(errorMsg);
      return;
    }

    // Nettoyer le player existant avant d'en créer un nouveau
    if (playerRef.current) {
      playerRef.current.pause();
      playerRef.current.removeEventListener('PlayerState' as any, handleStateChange);
      playerRef.current.removeEventListener('Error' as any, handleError);
      playerRef.current.removeEventListener('Ready' as any, handleReady);
      playerRef.current.delete();
      playerRef.current = null;
    }

    // Ne créer le player que si on a une URL valide
    if (!streamUrl) {
      setIsLoading(false);
      return;
    }

    try {
      // Réinitialiser l'état
      setIsLoading(true);
      setError(null);
      setIsConnected(false);

      // Création du player IVS
      const player = create({
        wasmWorker: '/amazon-ivs-wasmworker.min.js',
        wasmBinary: '/amazon-ivs-wasmworker.min.wasm'
      });

      playerRef.current = player;

      // Attachement au élément vidéo
      player.attachHTMLVideoElement(videoRef.current);

      // Configuration du player
      player.setAutoplay(autoplay);
      player.setMuted(false);
      player.setVolume(0.8);

      // Gestionnaires d'événements
      player.addEventListener('PlayerState' as any, handleStateChange);
      player.addEventListener('Error' as any, handleError);
      player.addEventListener('Ready' as any, handleReady);

      // Chargement de l'URL avec un léger délai pour laisser le player s'initialiser
      setTimeout(() => {
        if (playerRef.current) {
          player.load(streamUrl);
        }
      }, 100);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
      onPlayerError?.(err instanceof Error ? err.message : 'Erreur inconnue');
    }

    return () => {
      if (playerRef.current) {
        playerRef.current.pause();
        playerRef.current.removeEventListener('PlayerState' as any, handleStateChange);
        playerRef.current.removeEventListener('Error' as any, handleError);
        playerRef.current.removeEventListener('Ready' as any, handleReady);
        playerRef.current.delete();
        playerRef.current = null;
      }
    };
  }, [streamUrl, autoplay, handleStateChange, handleError, handleReady]);

  const handleRetry = useCallback(() => {
    if (retryCount < 3 && streamUrl) {
      setRetryCount(prev => prev + 1);
      setError(null);
      setIsLoading(true);
      setIsConnected(false);

      if (playerRef.current) {
        playerRef.current.load(streamUrl);
      }
    }
  }, [retryCount, streamUrl]);

  const defaultStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    backgroundColor: 'black',
    borderRadius: '10px',
    position: 'relative',
    overflow: 'hidden',
    ...style
  };

  if (error) {
    return (
      <Box sx={defaultStyle}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            color: 'white',
            padding: 2
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Erreur de connexion au stream
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: '#ccc' }}>
            {error}
          </Typography>
          {retryCount < 3 && (
            <Button
              variant="contained"
              size="small"
              onClick={handleRetry}
              sx={{ backgroundColor: '#333' }}
            >
              Réessayer ({retryCount}/3)
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={defaultStyle}>
      <video
        ref={videoRef}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover'
        }}
        playsInline
        controls={controls}
      />

      {(isLoading && !isConnected) && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            color: 'white',
            zIndex: 10
          }}
        >
          <CircularProgress size={60} sx={{ color: 'white', mb: 2 }} />
          <Typography variant="h6">
            Connexion au stream{dots}
          </Typography>
          <Typography variant="body2" sx={{ color: '#ccc', mt: 1 }}>
            Chargement du flux vidéo...
          </Typography>
        </Box>
      )}
    </Box>
  );
};
